import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import Table from "components/molecules/Table";
import TableLayout from "components/molecules/TableLayout";
import {ReportsWrap} from "components/atoms/Reports/Reports.styles";
import ReportsHeader from "components/atoms/Reports/ReportsHeader";
import RepotsDetail from "../components/atoms/Reports/ReportDetail/Index";
import ModalContainer from "components/molecules/ModalContainer";
import StoreSummary from "components/atoms/Reports/StoreSummary";
import WeeklyReport from "components/atoms/Reports/WeeklyReport";
import analyticsService from "services/analyticsService";
import Toast from "components/molecules/Toast";

// ─── Status badge ────────────────────────────────────────────────────────────
const STATUS_LABELS = {
    delivered: "Completed",
    cancelled: "Cancelled",
    out_for_delivery: "On Delivery",
    preparing: "Preparing",
    accepted: "Accepted",
    new_order: "New Order",
    ready_for_pickup: "Ready",
};
const STATUS_CLASS = {
    delivered: "completed",
    cancelled: "cancelled",
    out_for_delivery: "on-delivery",
    preparing: "preparing",
    accepted: "accepted",
    new_order: "new",
    ready_for_pickup: "pickup",
};

const StatusBadge = status => (
    <span className={`status ${STATUS_CLASS[status] || "pending"}`}>{STATUS_LABELS[status] || status || "—"}</span>
);

const formatTime = dateStr => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
};

// ─── Component ───────────────────────────────────────────────────────────────
const Reports = () => {
    const businessId = useSelector(state => state.business.businessId);
    const defaultStoreId = useSelector(state => state.business.defaultStore);
    const selectedStoreId = useSelector(state => state.business.selectedStoreId);
    const activeStoreId = selectedStoreId || defaultStoreId;

    const [activeTab, setActiveTab] = useState("Daily Report");

    // Shared filter state (driven by ReportsHeader)
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        search: "",
    });
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(filters.search);
        }, 500);
        return () => clearTimeout(handler);
    }, [filters.search]);

    // Daily report state
    const [dailyOrders, setDailyOrders] = useState([]);
    const [dailyLoading, setDailyLoading] = useState(false);
    const [dailyPagination, setDailyPagination] = useState({
        page: 1,
        itemsPerPage: 7,
        totalCount: 0,
    });

    const [exportLoading, setExportLoading] = useState(false);

    // ─── Fetch daily report ─────────────────────────────────────────────────
    const fetchDailyReport = useCallback(
        async ({page = 1, limit = 7} = {}) => {
            if (!businessId) return;
            setDailyLoading(true);
            try {
                const res = await analyticsService.getDailyReport({
                    restaurantId: businessId,
                    storeId: activeStoreId || undefined,
                    page,
                    limit,
                    search: debouncedSearch || undefined,
                    startDate: filters.startDate || undefined,
                    endDate: filters.endDate || undefined,
                    status: "delivered", // Explicitly for frontend clarity
                });
                setDailyOrders(res?.data?.orders || []);
                setDailyPagination(prev => ({
                    ...prev,
                    page,
                    itemsPerPage: limit,
                    totalCount: res?.data?.pagination?.totalOrders || 0,
                }));
            } catch (err) {
                Toast({type: "error", message: err.message || "Failed to load daily report"});
            } finally {
                setDailyLoading(false);
            }
        },
        [businessId, activeStoreId, filters, debouncedSearch]
    );

    // Re-fetch when tab becomes active or filters change
    useEffect(() => {
        if (activeTab === "Daily Report") {
            fetchDailyReport({page: 1, limit: dailyPagination.itemsPerPage});
        }
    }, [activeTab, debouncedSearch, filters.startDate, filters.endDate, businessId, activeStoreId]);

    // ─── Filter / pagination handler ────────────────────────────────────────
    const handleFiltersChange = useCallback(({startDate, endDate, search}) => {
        setFilters({startDate, endDate, search});
        // Reset to page 1 whenever filters change
        setDailyPagination(prev => ({...prev, page: 1}));
    }, []);

    const handlePaginationChange = useCallback(
        ({page, itemsPerPage}) => {
            setDailyPagination(prev => ({...prev, page, itemsPerPage}));
            fetchDailyReport({page, limit: itemsPerPage});
        },
        [fetchDailyReport]
    );

    // ─── Export ─────────────────────────────────────────────────────────────
    const handleExport = useCallback(() => {
        setExportLoading(true);
        try {
            analyticsService.exportReport({
                restaurantId: businessId || undefined,
                storeId: activeStoreId || undefined,
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined,
            });
        } finally {
            // window.open is synchronous; reset after a short delay for UX
            setTimeout(() => setExportLoading(false), 1500);
        }
    }, [businessId, activeStoreId, filters]);

    // ─── Table rows (memoised) ───────────────────────────────────────────────
    const columnNames = ["Order ID", "Time", "Customer", "Total Items", "Total Sale", "Status", "Details"];

    const rowsData = useMemo(
        () =>
            dailyOrders.map(order => [
                order.trackingId || String(order.orderId).slice(-8).toUpperCase(),
                formatTime(order.createdAt),
                order.customerName || "Guest",
                order.totalItems,
                `PKR ${Number(order.totalSale || 0).toFixed(2)}`,
                StatusBadge(order.status),
                // Details modal trigger
                <ModalContainer
                    key={order.orderId}
                    width="800"
                    title="Order Detail"
                    content={() => <RepotsDetail orderId={order.orderId} />}
                    btnComponent={({onClick}) => (
                        <span className="status pending" style={{cursor: "pointer"}} onClick={onClick}>
                            View
                        </span>
                    )}
                />,
            ]),
        [dailyOrders]
    );

    return (
        <>
            <ReportsHeader
                onFiltersChange={handleFiltersChange}
                onExport={handleExport}
                exportLoading={exportLoading}
            />
            <ReportsWrap>
                <div className="tabs">
                    <strong
                        className={activeTab === "Daily Report" ? "active" : ""}
                        onClick={() => setActiveTab("Daily Report")}>
                        Daily Report
                    </strong>
                    <strong
                        className={activeTab === "Weekly Report" ? "active" : ""}
                        onClick={() => setActiveTab("Weekly Report")}>
                        Weekly Report
                    </strong>
                    <strong
                        className={activeTab === "Store Summary" ? "active" : ""}
                        onClick={() => setActiveTab("Store Summary")}>
                        Store Summary
                    </strong>
                </div>

                {activeTab === "Daily Report" && (
                    <TableLayout
                        onChangeFilters={({page, itemsPerPage}) => handlePaginationChange({page, itemsPerPage})}
                        currentPage={dailyPagination.page}
                        pageSize={dailyPagination.itemsPerPage}
                        totalCount={dailyPagination.totalCount}
                        loading={dailyLoading}>
                        <Table
                            rowsData={rowsData}
                            columnNames={columnNames}
                            noPadding
                            loading={dailyLoading}
                            emptyMessage="No orders found for the selected period."
                        />
                    </TableLayout>
                )}

                {activeTab === "Weekly Report" && (
                    <WeeklyReport
                        restaurantId={businessId}
                        storeId={activeStoreId}
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                    />
                )}

                {activeTab === "Store Summary" && (
                    <StoreSummary
                        restaurantId={businessId}
                        storeId={activeStoreId}
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                    />
                )}
            </ReportsWrap>
        </>
    );
};

export default Reports;
